import { useCallback, useEffect, useMemo, useRef } from 'react';

import type {
  FilterModel,
  GridApi,
  IServerSideGetRowsParams,
} from 'ag-grid-community';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { fetchAdvancedAccounts } from '@/services/accounts';
import { buildFilterParam } from '@/utils/query';

const ACCOUNT_FILTER_FIELD_MAPPINGS: Record<string, string> = {
  primaryContact: 'primaryContact.fullName',
  csr: 'customerServiceRepresentative.fullName',
};

const ADDRESS_FILTER_FIELD_MAPPINGS: Record<string, string> = {
  city: 'city',
  stateOrProvince: 'stateOrProvince',
};

const ADDRESS_FILTER_FIELDS = new Set(
  Object.keys(ADDRESS_FILTER_FIELD_MAPPINGS),
);

const ACTIVE_STATE_CODE = 0;

const SEARCH_BY_FILTER_FIELDS: Record<string, string> = {
  name: 'name',
  code: 'code',
  contactName: 'primaryContact',
};

const SEARCH_BY_FILTER_FIELDS_V2: Record<string, string> = {
  ...SEARCH_BY_FILTER_FIELDS,
  phoneNumber: 'phoneNumber',
  ranchAddress: 'ranchAddress.composite',
  billingAddress: 'billingAddress.composite',
};

function normalizeAccountStatusFilter(
  filterModel: FilterModel | null | undefined,
): FilterModel | null | undefined {
  const accountStatusFilter = filterModel?.['accountStatus'] as
    | Record<string, unknown>
    | undefined;

  if (
    accountStatusFilter?.filterType !== 'set' ||
    !Array.isArray(accountStatusFilter.values) ||
    !accountStatusFilter.values.some((value) => value == null)
  ) {
    return filterModel;
  }

  const values = accountStatusFilter.values.filter((value) => value != null);
  const nextFilterModel = { ...filterModel };

  nextFilterModel['accountStatus'] =
    values.length > 0
      ? {
          operator: 'OR',
          conditions: [
            { filterType: 'set', values },
            { filterType: 'number', type: 'blank' },
          ],
        }
      : { filterType: 'number', type: 'blank' };

  return nextFilterModel;
}

export function buildAdvancedAccountFilterParam(
  filterModel: FilterModel | null | undefined,
) {
  return buildFilterParam(
    normalizeAccountStatusFilter(filterModel),
    ACCOUNT_FILTER_FIELD_MAPPINGS,
  );
}

function getStatusFilterValues(filterModel: FilterModel | null | undefined) {
  const values = filterModel?.stateCode?.values;

  if (!Array.isArray(values)) {
    return null;
  }

  return values.map(Number).filter((value) => Number.isFinite(value));
}

function getEffectiveHideInactive(
  hideInactive: boolean,
  filterModel: FilterModel | null | undefined,
) {
  const values = getStatusFilterValues(filterModel);

  if (!values) {
    return hideInactive;
  }

  return values.length === 1 && values[0] === ACTIVE_STATE_CODE;
}

function splitFilterModel(filterModel: FilterModel | null | undefined) {
  const accountFilterModel: FilterModel = {};
  const addressFilterModel: FilterModel = {};

  for (const [field, filter] of Object.entries(filterModel ?? {})) {
    if (ADDRESS_FILTER_FIELDS.has(field)) {
      addressFilterModel[field] = filter;
    } else {
      accountFilterModel[field] = filter;
    }
  }

  return { accountFilterModel, addressFilterModel };
}

function addTextSearchFilter(
  filterModel: FilterModel,
  field: string,
  search: string,
) {
  if (!search.trim()) return;

  filterModel[field] = {
    filterType: 'text',
    type: 'contains',
    filter: search.trim(),
  };
}

function addActiveStatusFilter(filterModel: FilterModel) {
  if (filterModel.stateCode) return;

  filterModel.stateCode = {
    filterType: 'set',
    values: [String(ACTIVE_STATE_CODE)],
  };
}

type AdvancedAccountDataSourceProps = {
  searchBy: string;
  searchTerm: string;
  hideInactive: boolean;
};

export const useAdvancedAccountDataSource = ({
  searchBy,
  searchTerm,
  hideInactive,
}: AdvancedAccountDataSourceProps) => {
  const apiRef = useRef<{ api: GridApi } | null>(null);
  const isEmbryoOwnerAlternateSearchEnabled = useFeatureFlag(
    'Alternate_Account_Lookup',
  );

  const getRows = useCallback(
    ({ request, success, fail, api }: IServerSideGetRowsParams) => {
      if (
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        success({ rowData: [], rowCount: 0 });
        return;
      }

      apiRef.current = { api };

      const sortEntry = request.sortModel[0];
      const orderBy = sortEntry
        ? (api.getColumn(sortEntry.colId)?.getColDef().field ??
          ACCOUNT_FILTER_FIELD_MAPPINGS[sortEntry.colId] ??
          ADDRESS_FILTER_FIELD_MAPPINGS[sortEntry.colId])
        : undefined;
      const orderDirection = sortEntry?.sort as 'asc' | 'desc' | undefined;
      const { accountFilterModel, addressFilterModel } = splitFilterModel(
        request.filterModel as FilterModel | null,
      );
      const effectiveHideInactive = getEffectiveHideInactive(
        hideInactive,
        request.filterModel as FilterModel | null,
      );

      const hasLeadingWildcard = searchTerm.startsWith('*');
      const hasTrailingWildcard = searchTerm.endsWith('*');
      const cleanSearchTerm = searchTerm.replace(/^\*+|\*+$/g, '');
      const actualSearchTerm = cleanSearchTerm || searchTerm;

      let search = actualSearchTerm;
      if (hasLeadingWildcard && !hasTrailingWildcard) {
        search = `*${actualSearchTerm}`;
      } else if (hasTrailingWildcard && !hasLeadingWildcard) {
        search = `${actualSearchTerm}*`;
      } else if (hasLeadingWildcard && hasTrailingWildcard) {
        search = `*${actualSearchTerm}*`;
      }

      const filterFields = isEmbryoOwnerAlternateSearchEnabled
        ? SEARCH_BY_FILTER_FIELDS_V2
        : SEARCH_BY_FILTER_FIELDS;
      const searchByFilterField = filterFields[searchBy];
      if (searchByFilterField) {
        addTextSearchFilter(accountFilterModel, searchByFilterField, search);
      }

      if (effectiveHideInactive) {
        addActiveStatusFilter(accountFilterModel);
      }

      fetchAdvancedAccounts({
        search:
          searchByFilterField ? undefined : search,
        skip: request.startRow,
        take: request.endRow - request.startRow,
        order: orderBy ? `${orderBy}:${orderDirection ?? 'asc'}` : undefined,
        filter: buildFilterParam(
          normalizeAccountStatusFilter(accountFilterModel),
          ACCOUNT_FILTER_FIELD_MAPPINGS,
        ),
        addressFilter: buildFilterParam(
          addressFilterModel,
          ADDRESS_FILTER_FIELD_MAPPINGS,
        ),
        v2: isEmbryoOwnerAlternateSearchEnabled,
      })
        .then((res) => {
          success({
            rowData: res.items,
            rowCount: res.totalCount,
          });
        })
        .catch(() => {
          fail();
        });
    },
    [hideInactive, isEmbryoOwnerAlternateSearchEnabled, searchBy, searchTerm],
  );

  const refetch = useCallback(() => {
    if (apiRef.current?.api) {
      apiRef.current.api.refreshServerSide({ purge: true });
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [searchBy, searchTerm, hideInactive, refetch]);

  return useMemo(
    () => ({
      dataSource: { getRows },
      refetch,
    }),
    [getRows, refetch],
  );
};
